import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ActiveProjectsService, { DEFAULT_ALLERGEN_ONLOAD_VERSION } from "@/services/activeProjectsService";
import { decodeHtmlEntities } from "@/utils/helper";
import AllergensAndSensitivitiesService from "@/services/allergensAndSensitivitiesService";
import type {
  SaveSiteAllergensRequest,
  SiteSectionData,
  SiteOption,
  AllergensSensitivitiesByAssessmentResponse,
} from "@/components/ActiveProjects/AllergensAndSensitivities/types";
import { transformApiAllergenDeclarations } from "@/constants/allergenDeclarationOptions";
import { useGlobalStateContext } from "@/context/GlobalContext";
import {
  enrichSiteSectionsWithProductInfo,
  getSiteAllergenTabsForAssessmentId,
} from "@/utils/productInfoDetailsSelectors";
import { AvailableTransitions } from "@/types/Common.types";
import { vendorContactService } from "@/services";
import useActiveProjectValidation from "@/components/ActiveProjects/validation/useActiveProjectValidation";
import { useLanguageContext } from "@/context/LanguageContext";

export interface AllergenData {
  id: string;
  allergen: string;
  allergenDescription?: string;
  label?: string; // Simple allergen name (e.g., "Egg")
  isSpecify?: string; // Whether specify field should be enabled (from API: "true" or "false")
  riskStatement: string;
  formalControlProgram: string;
  declared: string;
  labelDeclaration: string;
  specify: string;
  source: string;
  allergenDeclarationId?: string; // backend declaration row id
  attachment?: {
    id: string;
    filename: string;
    fileId?: string;
    downloadUrl?: string;
    previewUrl?: string;
  } | null;
  lastModified?: string;
  modifiedBy?: string;
  isCustom?: boolean;
}

export interface AllergenDeclarationData {
  selectedAllergen: string;
  contains: string;
  mayContain: string;
  otherAllergenDeclarations: Array<{
    option: {
      id: string;
      label: string;
      template: Array<{
        type: "text" | "input";
        value?: string;
      }>;
    };
    inputs: (string | null)[];
  }>;
  textFields: Record<string, string>;
  availableTransitions?: AvailableTransitions;
}

export interface AllergensPayload {
  metadata: {
    lastSaved: string;
    version: string;
    totalAllergens: number;
    hasAllergenUpdates: boolean;
    hasDeclarationData: boolean;
    productId: string | number;
    variantId: string | number;
  };
  updatedAllergens: AllergenData[];
  allergenDeclarations: Record<string, AllergenDeclarationData & {
    lastModified: string;
    modifiedBy: string;
  }>;
  summary: {
    totalAllergens: number;
    declaredAllergens: number;
    updatedCount: number;
    containsStatement: string;
    mayContainStatement: string;
    otherDeclarationsCount: number;
  };
}

export interface UseAllergensReturn {
  // Data
  allergensData: AllergenData[];
  allergenDeclarationData: AllergenDeclarationData | undefined;
  updatedAllergens: AllergenData[];
  allergenDeclarationOptions: any[]; // Options for "Other Allergen Declarations" dropdown
  sites: Array<{ siteId: string; siteName: string }>; // Sites from onload API
  siteSections: SiteSectionData[]; // Site sections from API
  transitionObject?: { data: any[] }; // Transition object from GET API response
  
  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  
  // Actions
  loadAllergens: (sectionId?: string | number, options?: LoadAllergensOptions) => Promise<void>;
  updateAllergen: (allergenData: AllergenData) => Promise<void>;
  saveAllAllergens: (declarationData?: AllergenDeclarationData, opts?: { 
    vendorAssessmentId: string | number;
    siteAllergenTabId: string | number;
    sectionName: string;
    sites?: Array<{ id: number | string }>;
    allergensDeclaration?: Array<{ id: number | string }>;
    transitionObject?: { objectId: string | number; transitionId: string };
    isAllergensValidated?: boolean;
    silentFlush?: boolean;
  }) => Promise<void>;
  
  // State management
  setUpdatedAllergens: React.Dispatch<React.SetStateAction<AllergenData[]>>;
  setAllergenDeclarationData: React.Dispatch<React.SetStateAction<AllergenDeclarationData | undefined>>;
  /**
   * Allows consumers to apply backend merges without triggering per-allergen API updates.
   * (Used by the Allergens & Sensitivities tab when merging declaration data into rows.)
   */
  setAllergensData: React.Dispatch<React.SetStateAction<AllergenData[]>>;
  setIsUserEditingDeclarations: (editing: boolean) => void;
  
  // Computed values
  hasUnsavedChanges: boolean;
  allergenDeclarationSummary: AllergenDeclarationData | null;
}

export interface UseAllergensProps {
  productId: string | number;
  variantId: string | number;
  vendorAssessmentId?: string | number; // Vendor assessment ID for API calls
}

interface AllergensOnloadPayload {
  sites: Array<{ siteId: string; siteName: string }>;
  allergenDeclarationOptions: ReturnType<typeof transformApiAllergenDeclarations>;
  baseAllergensData: Array<{
    id: string;
    allergen: string;
    allergenDescription: string;
    label: string;
    isSpecify: string;
  }>;
}

export interface LoadAllergensOptions {
  /** Bypass module cache and refetch glossary onload + assessment APIs. */
  refresh?: boolean;
  /**
   * Product info snapshot to use for merging siteAllergen metadata (e.g. after createSiteAllergens
   * when context setState has not flushed yet).
   */
  productInfoSnapshot?: unknown;
}

/** Map glossary onload `allergyDietaryQuestions` to datatable row shape (same as getAllergensData). */
const mapAllergyDietaryQuestionsToBaseRows = (
  allergyDietaryQuestions: Array<Record<string, unknown>> | undefined
) =>
  (allergyDietaryQuestions ?? []).map((item) => ({
    id: String(item?.id ?? ""),
    allergen: String(item?.name ?? ""),
    allergenDescription: String(item?.description ?? ""),
    label: String(item?.label ?? ""),
    isSpecify: String(item?.isSpecify ?? "false"),
  }));

type AllergensOnloadCacheEntry = {
  key: string;
  promise: Promise<AllergensOnloadPayload> | null;
  data: AllergensOnloadPayload | null;
};

type AllergensAssessmentCacheEntry = {
  key: string;
  promise: Promise<AllergensSensitivitiesByAssessmentResponse> | null;
  data: AllergensSensitivitiesByAssessmentResponse | null;
};

/** Survives component remounts (e.g. VendorTabs key churn) — dedupes G1 onload per assessment. */
const allergensOnloadModuleCache: AllergensOnloadCacheEntry = {
  key: "",
  promise: null,
  data: null,
};

/** Survives component remounts — dedupes G2 allergensSensitivities per assessment. */
const allergensAssessmentModuleCache: AllergensAssessmentCacheEntry = {
  key: "",
  promise: null,
  data: null,
};

/** Clear cached G2 assessment response (e.g. after save so section switch reads fresh data). */
export function invalidateAllergensAssessmentCache(vendorAssessmentId: string | number) {
  const requestKey = String(vendorAssessmentId);
  if (allergensAssessmentModuleCache.key === requestKey) {
    allergensAssessmentModuleCache.data = null;
    allergensAssessmentModuleCache.promise = null;
  }
}

/** Read cached G2 assessment response without triggering a network call (e.g. audit log ID collection). */
export function getCachedAllergensAssessmentResponse(
  vendorAssessmentId?: string | number
): AllergensSensitivitiesByAssessmentResponse | null {
  if (vendorAssessmentId == null || String(vendorAssessmentId).trim() === "") return null;
  const key = String(vendorAssessmentId);
  return allergensAssessmentModuleCache.key === key ? allergensAssessmentModuleCache.data : null;
}

/** Clear cached GET responses for an assessment (call after add/delete section). */
export function invalidateAllergensLoadCache(vendorAssessmentId: string | number) {
  // Onload cache is keyed by `vendorAssessmentId|version`; callers don't always know the version,
  // so clear unconditionally.
  allergensOnloadModuleCache.key = "";
  allergensOnloadModuleCache.data = null;
  allergensOnloadModuleCache.promise = null;
  invalidateAllergensAssessmentCache(vendorAssessmentId);
}

async function fetchAllergensOnloadModule(
  assessmentId: string | number,
  version: string | undefined,
  refresh = false
): Promise<AllergensOnloadPayload> {
  const requestKey = `${String(assessmentId)}|${String(version ?? "").trim()}`;

  if (allergensOnloadModuleCache.key !== requestKey) {
    allergensOnloadModuleCache.key = requestKey;
    allergensOnloadModuleCache.promise = null;
    allergensOnloadModuleCache.data = null;
  } else if (refresh) {
    allergensOnloadModuleCache.data = null;
    allergensOnloadModuleCache.promise = null;
  }

  if (allergensOnloadModuleCache.data) {
    return allergensOnloadModuleCache.data;
  }

  if (!allergensOnloadModuleCache.promise) {
    allergensOnloadModuleCache.promise = (async () => {
      const onloadData = await ActiveProjectsService.getAllergensOnloadData(assessmentId, version);
      const payload: AllergensOnloadPayload = {
        sites: onloadData?.sites || [],
        allergenDeclarationOptions: transformApiAllergenDeclarations(
          onloadData?.otherAllergenDeclarations || []
        ),
        baseAllergensData: mapAllergyDietaryQuestionsToBaseRows(onloadData?.allergyDietaryQuestions),
      };

      if (allergensOnloadModuleCache.key === requestKey) {
        allergensOnloadModuleCache.data = payload;
      }

      return payload;
    })();
  }

  try {
    return await allergensOnloadModuleCache.promise;
  } finally {
    if (allergensOnloadModuleCache.key === requestKey) {
      allergensOnloadModuleCache.promise = null;
    }
  }
}

async function fetchAllergensAssessmentModule(
  assessmentId: string | number,
  refresh = false
): Promise<AllergensSensitivitiesByAssessmentResponse> {
  const requestKey = String(assessmentId);

  if (allergensAssessmentModuleCache.key !== requestKey) {
    allergensAssessmentModuleCache.key = requestKey;
    allergensAssessmentModuleCache.promise = null;
    allergensAssessmentModuleCache.data = null;
  } else if (refresh) {
    allergensAssessmentModuleCache.data = null;
    allergensAssessmentModuleCache.promise = null;
  }

  if (allergensAssessmentModuleCache.data) {
    return allergensAssessmentModuleCache.data;
  }

  if (!allergensAssessmentModuleCache.promise) {
    allergensAssessmentModuleCache.promise = (async () => {
      const data = await AllergensAndSensitivitiesService.getAllergensSensitivitiesByAssessmentId(
        assessmentId
      );

      if (allergensAssessmentModuleCache.key === requestKey) {
        allergensAssessmentModuleCache.data = data;
      }

      return data;
    })();
  }

  try {
    return await allergensAssessmentModuleCache.promise;
  } finally {
    if (allergensAssessmentModuleCache.key === requestKey) {
      allergensAssessmentModuleCache.promise = null;
    }
  }
}

/**
 * Module-wise hook for Allergens & Sensitivities tab
 * Handles all allergen-related operations including API calls, data merging, and state management
 */
const useAllergens = (
  productId: string | number,
  variantId: string | number,
  vendorAssessmentId?: string | number
): UseAllergensReturn => {
  // Global loading context
  const {
    setIsLoading: setGlobalLoading,
    selectedActiveProject,
    activeProjectsProductInfo,
  } = useGlobalStateContext();
  const { languageText } = useLanguageContext();
  
  // State
  const [allergensData, setAllergensData] = useState<AllergenData[]>([]);
  const [allergenDeclarationData, setAllergenDeclarationData] = useState<AllergenDeclarationData>();
  const [updatedAllergens, setUpdatedAllergens] = useState<AllergenData[]>([]);
  const [allergenDeclarationOptions, setAllergenDeclarationOptions] = useState<any[]>([]); // Will be loaded from API
  const [sites, setSites] = useState<Array<{ siteId: string; siteName: string }>>([]); // Sites from onload API
  const [siteSections, setSiteSections] = useState<SiteSectionData[]>([]); // Site sections from API
  const [transitionObject, setTransitionObject] = useState<{ data: any[] } | undefined>(undefined); // Transition object from GET API
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateSectionValidationFlag } = useActiveProjectValidation();
  
  // Track when user is actively editing declarations to prevent overwrites
  const [isUserEditingDeclarations, setIsUserEditingDeclarations] = useState(false);

  /** Latest assessment id from props — in-flight loads compare so stale vendor responses do not apply. */
  const latestVendorAssessmentIdRef = useRef<string>("");

  useLayoutEffect(() => {
    latestVendorAssessmentIdRef.current =
      vendorAssessmentId != null && String(vendorAssessmentId).trim() !== ""
        ? String(vendorAssessmentId)
        : "";
  }, [vendorAssessmentId]);

  // Business logic for generating allergen declaration summary
  const generateAllergenDeclarationSummary = useCallback((allergensDataList: AllergenData[]) => {
    let contains: string[] = [];
    let mayContain: string[] = [];
    
    console.log("🔍 Processing allergens for declaration summary:", allergensDataList.length, "allergens");
    
    allergensDataList.forEach(allergen => {
      const declared = allergen.declared;
      const labelDeclaration = allergen.labelDeclaration;
      const allergenLabel = allergen.label || allergen.allergen; // Prefer label over allergen (name with HTML)
      const specify = allergen.specify;

      // Business Rule 1: DECLARED="YES" and LABEL_DECLARATION="Contains" 
      if (declared === "Yes" && labelDeclaration === "Contains") {
        let valueToAdd = "";
        if (specify && specify !== "-" && specify.trim() !== "") {
          valueToAdd = specify;
        } else {
          valueToAdd = allergenLabel || "";
        }
        
        if (valueToAdd && !contains.map(item => item.toLowerCase()).includes(valueToAdd.toLowerCase())) {
          contains.push(valueToAdd.toUpperCase());
        }
      }
      
      // Business Rule 2: DECLARED="YES" and LABEL_DECLARATION="May Contains"
      if (declared === "Yes" && (labelDeclaration === "May Contains" || labelDeclaration === "May Contain")) {
        let valueToAdd = "";
        if (specify && specify !== "-" && specify.trim() !== "") {
          valueToAdd = specify;
        } else {
          valueToAdd = allergenLabel || "";
        }
        
        if (valueToAdd && !mayContain.map(item => item.toLowerCase()).includes(valueToAdd.toLowerCase())) {
          mayContain.push(valueToAdd.toUpperCase());
        }
      }
    });

    const result: AllergenDeclarationData = {
      selectedAllergen: "Summary",
      contains: contains.join(", "),
      mayContain: mayContain.join(", "),
      otherAllergenDeclarations: [],
      textFields: {}
    };
    
    console.log("📊 Generated declaration summary:", {
      contains: result.contains,
      mayContain: result.mayContain
    });
    
    return result;
  }, []);

  // Computed values
  const allergenDeclarationSummary = useMemo(() => {
    if (allergensData.length > 0) {
      return generateAllergenDeclarationSummary(allergensData);
    }
    return null;
  }, [allergensData, generateAllergenDeclarationSummary]);

  const hasUnsavedChanges = useMemo(() => {
    const hasAllergenUpdates = updatedAllergens.length > 0;
    const hasDeclarationChanges = allergenDeclarationData && (
      allergenDeclarationData.otherAllergenDeclarations?.length > 0 ||
      Object.keys(allergenDeclarationData.textFields || {}).length > 0
    );
    
    return hasAllergenUpdates || Boolean(hasDeclarationChanges);
  }, [updatedAllergens, allergenDeclarationData]);

  // Load allergens data from API with merging
  const loadAllergens = useCallback(async (sectionId?: string | number, options?: LoadAllergensOptions) => {
    const refresh = options?.refresh === true;
    setIsLoading(true);
    setGlobalLoading(true); // Global loading indicator
    setError(null);

    let requestKey = "";
    try {
      console.log("🔄 [useAllergens] Loading allergens data for:", { productId, variantId, vendorAssessmentId, sectionId, refresh });

      if (!vendorAssessmentId) {
        return;
      }

      requestKey = String(vendorAssessmentId);

      // G1: glossary onload (cached module-wide per assessment)
      let onloadSites: Array<{ siteId: string; siteName: string }> = [];
      let baseAllergensData: AllergensOnloadPayload["baseAllergensData"] = [];
      try {
        const productInfoRecord =
          options?.productInfoSnapshot ?? selectedActiveProject ?? activeProjectsProductInfo;
        const tabs = productInfoRecord
          ? getSiteAllergenTabsForAssessmentId(productInfoRecord, vendorAssessmentId)
          : [];
        const fromProductInfo = sectionId
          ? tabs.find((t) => String(t.id) === String(sectionId))
          : tabs[0];
        const fromLocalSection =
          sectionId != null
            ? siteSections.find((s) => String(s.id) === String(sectionId))
            : siteSections[0];
        const version =
          String(fromProductInfo?.allergensDeclarationVersion ?? "").trim() ||
          String(fromLocalSection?.allergensDeclarationVersion ?? "").trim() ||
          DEFAULT_ALLERGEN_ONLOAD_VERSION;

        const onloadPayload = await fetchAllergensOnloadModule(vendorAssessmentId, version, refresh);
        onloadSites = onloadPayload.sites;
        baseAllergensData = onloadPayload.baseAllergensData;

        if (requestKey === latestVendorAssessmentIdRef.current) {
          setSites(onloadPayload.sites);
          setAllergenDeclarationOptions(onloadPayload.allergenDeclarationOptions);
        }
      } catch (error) {
        console.error("❌ [useAllergens] Error loading allergen onload data:", error);
        if (requestKey === latestVendorAssessmentIdRef.current) {
          setSites([]);
          setAllergenDeclarationOptions([]);
        }
      }

      if (requestKey !== latestVendorAssessmentIdRef.current) {
        return;
      }

      // Transform base allergens to AllergenData format (these are the rows)
      const transformedBaseAllergens: AllergenData[] = baseAllergensData.map((item) => ({
        id: item.id || '',
        allergen: item.allergen || '',
        allergenDescription: item.allergenDescription || '',
        label: item.label || '',
        isSpecify: item.isSpecify || 'false',
        riskStatement: '',
        formalControlProgram: '',
        declared: '',
        labelDeclaration: '',
        specify: '',
        source: '',
      }));

      // G2: assessment sections — module cache; network skipped when data is already loaded
      const response = await fetchAllergensAssessmentModule(vendorAssessmentId, refresh);

      if (requestKey !== latestVendorAssessmentIdRef.current) {
        return;
      }

      // Extract transitionObject from response
      if (response?.transitionObject) {
        setTransitionObject(response.transitionObject);
      }
      
      // Transform API response
      const siteAllergenArray = response?.siteAllergen || [];
      
      // Helper function to format site name with site code
      const formatSiteNameWithCode = (site: any): string => {
        if (!site || !site.name) {
          return '';
        }
        
        const siteName = site.name;
        const notAvailableText = languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.NOT_AVAILABLE;
        let siteCode = notAvailableText;
        
        // Priority: site.id (siteId) > orbcSiteCode from sites array > site.siteCode > N/A
        if (site.id) {
          siteCode = String(site.id);
        } else {
          // Try to find site in sites array to get orbcSiteCode
          // Match by site.id or site.siteCode with s.siteId
          const matchingSite = onloadSites.find((s: any) => 
            (site.id && String(s.siteId) === String(site.id)) || 
            (site.siteCode && String(s.siteId) === String(site.siteCode))
          );
          if (matchingSite && (matchingSite as any).orbcSiteCode) {
            siteCode = String((matchingSite as any).orbcSiteCode);
          } else if (site.siteCode) {
            siteCode = String(site.siteCode);
          }
        }
        
        return `${siteName} (${siteCode})`;
      };

      // Transform site sections when loading all sections or after a forced refresh
      if (sectionId === undefined || refresh) {
        const transformedSections: SiteSectionData[] = siteAllergenArray.map((section: any) => {
          // Transform sites array to SiteOption format
          const siteOptions: SiteOption[] = section.sites?.map((site: any) => ({
            label: formatSiteNameWithCode(site),
            value: site.id || site.siteCode || '',
          })) || [];

          return {
            id: section.id,
            sectionName: section.sectionName || '',
            siteName: siteOptions,
            ...(section.allergensDeclarationVersion
              ? { allergensDeclarationVersion: String(section.allergensDeclarationVersion) }
              : {}),
          };
        });

        const productInfoRecord =
          options?.productInfoSnapshot ?? selectedActiveProject ?? activeProjectsProductInfo;
        const enrichedSections = enrichSiteSectionsWithProductInfo(
          transformedSections,
          productInfoRecord,
          vendorAssessmentId
        );

        setSiteSections(enrichedSections);
      }

      // Get the specific section by sectionId, or first section if not provided
      const targetSection = sectionId 
        ? siteAllergenArray.find((section: any) => String(section.id) === String(sectionId))
        : siteAllergenArray.find((section: any) => 
            section.allergensDeclaration && section.allergensDeclaration.length > 0
          ) || siteAllergenArray[0];

      let mergedAllergens: AllergenData[] = transformedBaseAllergens;
      let declarationData: AllergenDeclarationData = {
        selectedAllergen: "Summary",
        contains: "",
        mayContain: "",
        otherAllergenDeclarations: [],
        textFields: {}
      };

      // If section has allergensDeclaration data, merge it into base allergens
      if (targetSection && targetSection.allergensDeclaration && targetSection.allergensDeclaration.length > 0) {
        const allergensDeclarations = targetSection.allergensDeclaration;
        // Match declarations to base allergens by allergensIndicateThatApplyId.
        // Do not fall back to index – API order may differ from base list order; index fallback caused
        mergedAllergens = transformedBaseAllergens.map((baseAllergen) => {
          const matchingDeclaration = allergensDeclarations.find((decl: any) =>
            decl.allergensIndicateThatApplyId && String(decl.allergensIndicateThatApplyId) === String(baseAllergen.id)
          );

          if (!matchingDeclaration) {
            return baseAllergen;
          }

          // Merge specific fields from saved data
          return {
            ...baseAllergen,
            allergenDeclarationId: String(matchingDeclaration.id),
            riskStatement: matchingDeclaration.statementToBestDescribe || baseAllergen.riskStatement,
            formalControlProgram: matchingDeclaration.formalControlProgram || baseAllergen.formalControlProgram,
            declared: matchingDeclaration.declared || baseAllergen.declared,
            labelDeclaration: matchingDeclaration.labelDeclaration || baseAllergen.labelDeclaration,
            specify: matchingDeclaration.specify || baseAllergen.specify,
            source: matchingDeclaration.source || baseAllergen.source,
          };
        });

        // Transform declaration data - parse the JSON string in otherAlergenDeclaration
        // Explicitly check for null/undefined - if null, use empty array
        const otherDeclarations = (targetSection.otherAlergenDeclaration === null || targetSection.otherAlergenDeclaration === undefined) 
          ? [] 
          : (Array.isArray(targetSection.otherAlergenDeclaration) ? targetSection.otherAlergenDeclaration : []);
        const transformedOtherDeclarations = otherDeclarations.map((decl: any) => {
          try {
            // Parse the JSON string - it may be double-encoded
            let parsedText = decl.text;
            try {
              parsedText = JSON.parse(decl.text);
              // If it's still a string after first parse, try parsing again
              if (typeof parsedText === 'string') {
                parsedText = JSON.parse(parsedText);
              }
            } catch {
              // If parsing fails, use the text as-is
            }
            
            // Extract option and inputs from parsed structure
            const option = parsedText?.option || {};
            const inputs = parsedText?.inputs || [];
            
            return {
              option: {
                id: option?.name ?? option?.id ?? '',
                label: option?.label ?? decl.text ?? '',
                template: Array.isArray(option?.template) ? option.template : []
              },
              inputs: Array.isArray(inputs) ? inputs : []
            };
          } catch (error) {
            console.warn("⚠️ [useAllergens] Error parsing otherAlergenDeclaration:", error, decl);
            return {
              option: { id: '', label: decl.text || '', template: [] },
              inputs: [],
            };
          }
        });

        declarationData = {
          selectedAllergen: "Summary",
          contains: targetSection.contains || "",
          mayContain: targetSection.mayContain || "",
          otherAllergenDeclarations: transformedOtherDeclarations,
          textFields: {},
        };
      } else if (targetSection) {
        // Even if no allergensDeclaration, still set contains/mayContain and otherAlergenDeclaration if they exist
        // Explicitly check for null/undefined - if null, use empty array
        const otherDeclarations = (targetSection.otherAlergenDeclaration === null || targetSection.otherAlergenDeclaration === undefined) 
          ? [] 
          : (Array.isArray(targetSection.otherAlergenDeclaration) ? targetSection.otherAlergenDeclaration : []);
        
        const transformedOtherDeclarations = otherDeclarations.map((decl: any) => {
          try {
            // Parse the JSON string - it may be double-encoded
            let parsedText = decl.text;
            try {
              parsedText = JSON.parse(decl.text);
              // If it's still a string after first parse, try parsing again
              if (typeof parsedText === 'string') {
                parsedText = JSON.parse(parsedText);
              }
            } catch {
              // If parsing fails, use the text as-is
            }
            
            // Extract option and inputs from parsed structure
            const option = parsedText?.option || {};
            const inputs = parsedText?.inputs || [];
            
            return {
              option: {
                id: option?.name ?? option?.id ?? '',
                label: option?.label ?? decl.text ?? '',
                template: Array.isArray(option?.template) ? option.template : []
              },
              inputs: Array.isArray(inputs) ? inputs : []
            };
          } catch (error) {
            console.warn("⚠️ [useAllergens] Error parsing otherAlergenDeclaration:", error, decl);
            return {
              option: { id: '', label: decl.text || '', template: [] },
              inputs: [],
            };
          }
        });

        declarationData = {
          selectedAllergen: "Summary",
          contains: targetSection.contains || "",
          mayContain: targetSection.mayContain || "",
          otherAllergenDeclarations: transformedOtherDeclarations,
          textFields: {},
        };
      }

      // Decode HTML entities in merged allergen data
      const decodedAllergensData = decodeHtmlEntities(mergedAllergens);
      
      // Ensure otherAllergenDeclarations is always an array (never null/undefined)
      const finalDeclarationData: AllergenDeclarationData = {
        ...declarationData,
        otherAllergenDeclarations: Array.isArray(declarationData.otherAllergenDeclarations) 
          ? declarationData.otherAllergenDeclarations 
          : []
      };

      if (requestKey !== latestVendorAssessmentIdRef.current) {
        return;
      }

      setAllergensData(decodedAllergensData);
      setAllergenDeclarationData(finalDeclarationData);
      
      // console.log("✅ [useAllergens] Data loaded successfully:", {
      //   totalAllergens: response.allergensData.length,
      //   hasUpdates: response.hasUpdates,
      //   hasDeclarationData: !!response.declarationData
      // });
      
    } catch (err) {
      if (requestKey !== "" && requestKey !== latestVendorAssessmentIdRef.current) {
        // Another vendor/assessment load superseded this one — do not surface error or touch state.
        return;
      }
      const errorMessage = err instanceof Error ? err.message : "Failed to load allergens data";
      console.error("❌ [useAllergens] Error loading allergens:", err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setGlobalLoading(false); // Global loading indicator
    }
  }, [productId, variantId, vendorAssessmentId, setGlobalLoading, languageText, selectedActiveProject, activeProjectsProductInfo]);

  // Re-merge allergensDeclarationVersion when product info context refreshes (e.g. after loadProjectDetails).
  useEffect(() => {
    if (!vendorAssessmentId) return;
    const productInfoRecord = selectedActiveProject ?? activeProjectsProductInfo;
    if (!productInfoRecord) return;
    setSiteSections((prev) => {
      if (!prev.length) return prev;
      const enriched = enrichSiteSectionsWithProductInfo(prev, productInfoRecord, vendorAssessmentId);
      const unchanged = enriched.every(
        (section, index) =>
          section.allergensDeclarationVersion === prev[index]?.allergensDeclarationVersion
      );
      return unchanged ? prev : enriched;
    });
  }, [selectedActiveProject, activeProjectsProductInfo, vendorAssessmentId]);

  // Update individual allergen
  const updateAllergen = useCallback(async (allergenData: AllergenData) => {
    try {
      console.log("🔄 [useAllergens] Updating allergen:", allergenData.id);
      console.log("🔄 [useAllergens] Updating allergen:", allergenData);
      // Update in main data
      setAllergensData(prev => 
        prev.map(allergen => 
          allergen.id === allergenData.id ? { ...allergen, ...allergenData } : allergen
        )
      );
      
      // Track as updated
      setUpdatedAllergens(prev => {
        const existingIndex = prev.findIndex(a => a.id === allergenData.id);
        const updatedAllergen = {
          ...allergenData,
          lastModified: new Date().toISOString(),
          modifiedBy: "user@example.com"
        };
        
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = updatedAllergen;
          return updated;
        } else {
          return [...prev, updatedAllergen];
        }
      });
      
      // Call API to update individual allergen
      await ActiveProjectsService.updateAllergenData(allergenData);
      
      console.log("✅ [useAllergens] Allergen updated successfully");
      
    } catch (err) {
      console.error("❌ [useAllergens] Error updating allergen:", err);
      throw err;
    }
  }, []);

  // Note: Add new allergen functionality removed as per requirements

  // Save all allergens with comprehensive payload
  const saveAllAllergens = useCallback(async (declarationData?: AllergenDeclarationData, opts?: { 
    vendorAssessmentId: string | number;
    siteAllergenTabId: string | number;
    sectionName: string;
    sites?: Array<{ id: number | string }>;
    allergensDeclaration?: Array<{ id: number | string }>;
    transitionObject?: { objectId: string | number; transitionId: string };
    isAllergensValidated?: boolean;
    silentFlush?: boolean;
  }): Promise<void> => {
    const silent = opts?.silentFlush === true;
    if (!silent) {
      setIsSaving(true);
      setGlobalLoading(true);
    }
    setError(null);
    
    try {
      console.log("🔄 [useAllergens] Saving all allergens...");
      
      if (!opts?.vendorAssessmentId || !opts?.siteAllergenTabId || !opts?.sectionName) {
        throw new Error("Missing required parameters: vendorAssessmentId, siteAllergenTabId, and sectionName are required");
      }
      
      const currentDeclarationData = declarationData || allergenDeclarationData;

      // Build new site allergens payload structure - same for all products (petfood and non-petfood)
      const otherDecls = (currentDeclarationData?.otherAllergenDeclarations || []).map((decl, index) => {
        const textObject = {
          option: {
            id: decl?.option?.id || '',
            label: decl?.option?.label || '',
            template: Array.isArray(decl?.option?.template) ? decl.option.template : []
          },
          inputs: Array.isArray(decl?.inputs) ? decl.inputs : []
        };
        const text = JSON.stringify(textObject);
        return { index, text };
      }).filter(item => !!item.text);

      const sitePayload: SaveSiteAllergensRequest = {
        siteAllergenTabId: opts.siteAllergenTabId,
        sectionName: opts.sectionName,
        ...(opts.sites && opts.sites.length > 0 ? { sites: opts.sites } : {}),
        ...(otherDecls.length > 0 ? { otherAlergenDeclaration: otherDecls } : { otherAlergenDeclaration: [] }),
        ...(currentDeclarationData?.contains ? { contains: currentDeclarationData.contains } : {}),
        ...(currentDeclarationData?.mayContain ? { mayContain: currentDeclarationData.mayContain } : {}),
        ...(opts.allergensDeclaration && opts.allergensDeclaration.length > 0 ? { allergensDeclaration: opts.allergensDeclaration } : {}),
        ...(opts.isAllergensValidated !== undefined ? { isAllergensValidated: String(opts.isAllergensValidated) } : {})
      };
      console.log("📋 [useAllergens] Saving allergens:", sitePayload);

      console.log("📋 [useAllergens] Saving site allergens with new payload:", sitePayload);
      await AllergensAndSensitivitiesService.saveSiteAllergens(sitePayload, opts.vendorAssessmentId);
      console.log("✅ [useAllergens] Site allergens saved successfully");
      // Refetch G2 assessment cache so audit log and section switch read fresh server data.
      await fetchAllergensAssessmentModule(opts.vendorAssessmentId, true);
        if(opts.isAllergensValidated && opts?.siteAllergenTabId){
          updateSectionValidationFlag('allergens', opts?.siteAllergenTabId);
        }
      // Handle workflow transitions separately if needed (outside of payload)
      if (opts?.transitionObject) {
        await vendorContactService.applyTransition(opts.transitionObject);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to save allergens";
      console.error("❌ [useAllergens] Error saving allergens payload:", err);
      if (!silent) {
        setError(errorMessage);
      }
      throw err;
    } finally {
      if (!silent) {
        setIsSaving(false);
        setGlobalLoading(false); // Global loading indicator
      }
    }
  }, [allergenDeclarationData, setGlobalLoading]);

  // Load data on mount and when product/variant/vendorAssessmentId changes
  useEffect(() => {
    console.log("🔄 [useAllergens] Product/Variant/Assessment changed, clearing and reloading data:", { productId, variantId, vendorAssessmentId });

    setAllergenDeclarationData(undefined);
    setUpdatedAllergens([]);
    setError(null);

    if (vendorAssessmentId) {
      loadAllergens();
    }
  }, [productId, variantId, vendorAssessmentId]);

  // Merge only contains/mayContain from summary into existing declaration (non-destructive)
  useEffect(() => {
    if (!allergenDeclarationSummary || isUserEditingDeclarations) return;

    setAllergenDeclarationData(prev => {
      if (!prev) {
        // Initialize from summary when there is no existing data
        return allergenDeclarationSummary;
      }

      const needsUpdate = (
        prev.contains !== allergenDeclarationSummary.contains ||
        prev.mayContain !== allergenDeclarationSummary.mayContain
      );

      if (!needsUpdate) return prev;

      // Update only the two fields; preserve all user-entered fields verbatim
      return {
        ...prev,
        contains: allergenDeclarationSummary.contains,
        mayContain: allergenDeclarationSummary.mayContain,
      };
    });
  }, [allergenDeclarationSummary, isUserEditingDeclarations]);

  return {
    // Data
    allergensData,
    allergenDeclarationData,
    updatedAllergens,
    allergenDeclarationOptions,
    sites,
    siteSections,
    transitionObject,
    
    // Loading states
    isLoading,
    isSaving,
    error,
    
    // Actions
    loadAllergens,
    updateAllergen,
    saveAllAllergens,
    
    // State management
    setUpdatedAllergens,
    setAllergenDeclarationData,
    setAllergensData,
    setIsUserEditingDeclarations,
    
    // Computed values
    hasUnsavedChanges,
    allergenDeclarationSummary
  };
};

export default useAllergens;
